/*
  Based on documentation https://help.stoqapp.com/en/article/use-stoq-javascript-api-to-build-custom-notify-me-preorder-experiences-35g74t/
  we use a custom element to handle the out of stock functionality. Because we have some bug when we have hidden elements ( for example in product tabs )
  button is not initialized properly and we can't trigger the open modal function. So because of this we have to use a custom element to handle the out of stock functionality.
  If for some reason STOQ is not loaded, we have to fallback to a disabled button. It's standard out of stock functionality.
*/

if (!customElements.get('wlsn-out-of-stock')) {
  class WLSNOutOfStock extends HTMLElement {
    constructor() {
      super();
      this._onClick = this._onClick.bind(this);
      this._onStoqLoaded = this._onStoqLoaded.bind(this);
      this._pendingOpen = false;
      this._fallbackTimer = null;

      this.classes = {
        fallback: 'wlsn-oos--fallback',
        stoq: 'wlsn-oos__stoq',
      }
    }

    connectedCallback() {
      this.addEventListener('click', this._onClick);
      this._hideFallback();

      if (typeof window._RestockRocket?.openModal === 'function') {
        window.addEventListener('stoq:loaded', this._onStoqLoaded, { once: true });
        return;
      }

      window.addEventListener('stoq:loaded', this._onStoqLoaded, { once: true });
      this._scheduleNoStoqScriptFallback();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      window.removeEventListener('stoq:loaded', this._onStoqLoaded);
      this._clearFallbackTimer();
    }

    _clearFallbackTimer() {
      if (this._fallbackTimer != null) {
        clearTimeout(this._fallbackTimer);
        this._fallbackTimer = null;
      }
    }

    _scheduleNoStoqScriptFallback() {
      const raw = this.dataset.fallbackTimeout;
      const ms = raw != null && raw !== '' ? parseInt(raw, 10) : 4000;
      if (!Number.isFinite(ms) || ms <= 0) return;

      this._fallbackTimer = setTimeout(() => {
        this._fallbackTimer = null;
        if (typeof window._RestockRocket?.openModal !== 'function') {
          this._showFallback();
        }
      }, ms);
    }

    _showFallback() {
      this.classList.add(this.classes.fallback);
    }

    _hideFallback() {
      this.classList.remove(this.classes.fallback);
    }

    _onStoqLoaded(event) {
      this._clearFallbackTimer();

      if (event?.detail?.enabled === false) {
        this._showFallback();
      } else {
        this._hideFallback();
      }

      if (this._pendingOpen) {
        this._pendingOpen = false;
        if (event?.detail?.enabled !== false) {
          this._invokeOpenModal();
        }
      }
    }

    _getProductJson() {
      const el = this.querySelector('script[type="application/json"][data-stoq-product]');
      if (!el) return null;
      try {
        const parsed = JSON.parse(el.textContent);
        return parsed && typeof parsed === 'object' ? parsed : null;
      } catch {
        return null;
      }
    }

    _normalizeProductForStoq(raw, variantId) {
      if (!raw || typeof raw !== 'object') return null;
      const out = { ...raw };
      const vidStr = variantId != null && String(variantId).trim() !== '' ? String(variantId).trim() : null;
      const vidNum = vidStr != null ? Number(vidStr) : NaN;
      const variantStub =
        vidStr != null
          ? { id: Number.isNaN(vidNum) ? vidStr : vidNum, available: false }
          : null;

      if (!Array.isArray(out.variants)) {
        out.variants = variantStub ? [variantStub] : [];
      } else if (out.variants.length === 0 && variantStub) {
        out.variants = [variantStub];
      }

      return out;
    }

    _minimalStoqProduct(variantId) {
      const productId = this.getAttribute('data-product-id')?.trim();
      const vidStr = variantId != null && String(variantId).trim() !== '' ? String(variantId).trim() : null;
      if (!productId || !vidStr) return null;
      const pidNum = Number(productId);
      const vidNum = Number(vidStr);
      return {
        id: Number.isNaN(pidNum) ? productId : pidNum,
        variants: [{ id: Number.isNaN(vidNum) ? vidStr : vidNum, available: false }],
      };
    }

    _resolveVariantId() {
      if (this.getAttribute('data-variant-source') === 'form') {
        const form = this.closest('form');
        const input = form?.querySelector('input[name="id"]');
        const v = input?.value?.trim();
        return v || null;
      }
      const id = this.getAttribute('data-variant-id')?.trim();
      return id || null;
    }

    _customerData() {
      const email = this.getAttribute('data-customer-email')?.trim();
      if (!email) return null;
      return { email };
    }

    _findTrigger(target) {
      const el = target.closest('button, [role="button"], .js-stoq-notify-trigger');
      if (!el) return null;
      if (el.closest(`.${this.classes.fallback}`)) return null;
      return el;
    }

    _onClick(event) {
      const trigger = this._findTrigger(event.target);
      if (!trigger || !this.contains(trigger)) return;
      if (trigger.disabled || trigger.getAttribute('aria-disabled') === 'true') return;

      event.preventDefault();
      event.stopPropagation();

      if (typeof window._RestockRocket?.openModal === 'function') {
        this._invokeOpenModal();
        return;
      }

      this._pendingOpen = true;
    }

    _invokeOpenModal() {
      const rr = window._RestockRocket;
      if (typeof rr?.openModal !== 'function') return;

      const variantId = this._resolveVariantId();
      const customer = this._customerData();
      const productTitle = this.getAttribute('data-product-title');

      if (this.getAttribute('data-variant-source') === 'form' && !variantId) {
        return;
      }

      let raw = this._getProductJson();
      let product = raw ? this._normalizeProductForStoq(raw, variantId) : null;
      if (!product && variantId) {
        product = this._minimalStoqProduct(variantId);
      }
      if (product && variantId) {
        if (customer) {
          rr.openModal({title: productTitle, ...product}, String(variantId), customer);
        } else {
          rr.openModal({title: productTitle, ...product}, String(variantId));
        }
        return;
      }

      if (product && !variantId) {
        product = this._normalizeProductForStoq(product, null);
        const firstId = product.variants?.[0]?.id;
        if (firstId != null) {
          if (customer) {
            rr.openModal({title: productTitle, ...product}, String(firstId), customer);
          } else {
            rr.openModal({title: productTitle, ...product}, String(firstId));
          }
          return;
        }
        if (customer) {
          rr.openModal({title: productTitle, ...product}, undefined, customer);
        } else {
          rr.openModal({title: productTitle, ...product});
        }
        return;
      }

      rr.openModal();
    }
  }

  customElements.define('wlsn-out-of-stock', WLSNOutOfStock);
}
